import type { LegalBlock, LegalDocument, LegalDocumentType } from '@/types/legal'
import { PRIVACY_POLICY, TERMS_AND_CONDITIONS } from '@/data/mock/legal'

const FALLBACK: Record<LegalDocumentType, LegalDocument> = {
  privacy: PRIVACY_POLICY,
  terms: TERMS_AND_CONDITIONS,
}

function slugify(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 80) || 'section'
}

function extractTitleFromHtml(html: string): string | null {
  const headingMatch = html.match(/<(?:strong|h[1-4])[^>]*>([\s\S]*?)<\/(?:strong|h[1-4])>/i)
  if (headingMatch?.[1]) {
    const text = headingMatch[1].replace(/<[^>]+>/g, '').replace(/&nbsp;/gi, ' ').trim()
    if (text) return text
  }
  return null
}

function stripHtml(html: string): string {
  return html
    .replace(/<br\s*\/?>/gi, ' ')
    .replace(/<\/p>/gi, ' ')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

export function normalizeLegalDocument(type: LegalDocumentType, blocks: LegalBlock[]): LegalDocument {
  const fallback = FALLBACK[type]

  if (!blocks.length) {
    return fallback
  }

  const sections = blocks.map((block, index) => {
    const title = extractTitleFromHtml(block.html) ?? `Section ${index + 1}`
    return {
      id: `${slugify(title)}-${block.id}`,
      title,
      html: block.html,
    }
  })

  const introSource = stripHtml(blocks[0]?.html ?? '')
  const intro = introSource.length > 320 ? `${introSource.slice(0, 317)}…` : introSource

  return {
    ...fallback,
    intro: intro || fallback.intro,
    sections,
  }
}

export function legalFallback(type: LegalDocumentType): LegalDocument {
  return FALLBACK[type]
}
